// src/pages/tender/EngineerDashboard.js
import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { getAuth, onAuthStateChanged, signOut } from "firebase/auth";
import {
  collection,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { db } from "../../firebaseConfig";
import toast, { Toaster } from "react-hot-toast";

const cx = (...xs) => xs.filter(Boolean).join(" ");

function Badge({ color = "gray", children }) {
  const map = {
    green: "bg-emerald-100 text-emerald-700",
    yellow: "bg-amber-100 text-amber-700",
    red: "bg-red-100 text-red-700",
    gray: "bg-slate-100 text-slate-700",
    blue: "bg-sky-100 text-sky-700",
  };
  return (
    <span
      className={cx(
        "inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold",
        map[color] || map.gray
      )}
    >
      {children}
    </span>
  );
}

function StatCard({ label, value, note }) {
  return (
    <div className="rounded-2xl bg-white shadow border border-emerald-100 px-4 py-3">
      <div className="text-[11px] text-slate-500 uppercase tracking-wide">
        {label}
      </div>
      <div className="mt-1 flex items-end justify-between">
        <div className="text-xl font-extrabold text-emerald-800">{value}</div>
        {note && (
          <div className="text-[10px] text-slate-400 text-right max-w-[130px]">
            {note}
          </div>
        )}
      </div>
    </div>
  );
}

function ProgressBar({ value }) {
  const pct = Math.max(0, Math.min(100, Number(value || 0)));
  return (
    <div className="w-full h-2 rounded-full bg-gray-200 overflow-hidden">
      <div
        className={cx(
          "h-full rounded-full",
          pct < 25
            ? "bg-orange-400"
            : pct < 50
            ? "bg-yellow-400"
            : pct < 75
            ? "bg-sky-500"
            : "bg-emerald-500"
        )}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

const formatINR = (n) => `₹ ${Number(n || 0).toLocaleString("en-IN")}`;

export default function EngineerDashboard() {
  const navigate = useNavigate();
  const auth = useMemo(() => getAuth(), []);
  const [user, setUser] = useState(null);

  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(true);

  /* ---------- AUTH GUARD ---------- */
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!u) {
        toast.error("Please login as Engineer.");
        navigate("/tender/engineer", { replace: true });
        return;
      }
      setUser(u);
    });
    return () => unsub();
  }, [auth, navigate]);

  /* ---------- LOAD ALL PROJECTS (Awarded tenders) ---------- */
  useEffect(() => {
    const loadProjects = async () => {
      if (!user) return;
      try {
        setLoadingProjects(true);

        // ✅ Load all tenders
        const snap = await getDocs(collection(db, "tenders"));
        const list = [];
        snap.forEach((d) => list.push({ id: d.id, ...d.data() }));

        // ✅ Show all awarded projects (ya tum all bhi kar sakte ho)
        const awarded = list.filter((t) => t.awardedVendorUid);

        const rows = [];
        for (const t of awarded) {
          const tenderId = t.id;

          // 🔹 Engineer RA summary (अगर fail हो जाए तो bhi project दिखना चाहिए)
          let raCount = 0;
          let raTotal = 0;

          try {
            const raSnap = await getDocs(
              query(
                collection(db, "ra_bills_engineer"),
                where("tenderId", "==", tenderId)
              )
            );
            raSnap.forEach((rb) => {
              const r = rb.data();
              raCount++;
              raTotal += Number(r.totalAmount || 0);
            });
          } catch (e) {
            // Silent fail – project list still show
            console.warn("Engineer RA read skipped:", tenderId, e?.code || e?.message);
          }

          rows.push({
            id: tenderId,
            pacsName: t.ownerCompany || t.organization || "",
            projectTitle: t.title || t.projectName || "",
            district: t.city || t.district || "",
            state: t.state || "Gujarat",
            capacityMt: t.capacityMt || null,
            progress: t.constructionOverallProgress || 0,
            raCount,
            raTotal,
          });
        }

        setProjects(rows);
      } catch (e) {
        console.error(e);
        toast.error("Failed to load projects for Engineer.");
        setProjects([]);
      } finally {
        setLoadingProjects(false);
      }
    };

    loadProjects();
  }, [user]);

  const totals = useMemo(() => {
    const total = projects.length;
    const completed = projects.filter((p) => (p.progress || 0) >= 95).length;
    const inProgress = projects.filter((p) => (p.progress || 0) > 0 && (p.progress || 0) < 95).length;
    const notStarted = total - completed - inProgress;
    return { total, completed, inProgress, notStarted };
  }, [projects]);

  const doLogout = async () => {
    await signOut(auth);
    toast("Logged out");
    navigate("/tender/engineer", { replace: true });
  };

  if (!user) return null;

  const name = (user.email || "Engineer").split("@")[0];

  return (
    <>
      <Toaster position="top-center" />
      <div className="min-h-screen pb-8 bg-emerald-50">
        <div className="max-w-6xl mx-auto px-4 py-6 space-y-5">
          {/* HEADER */}
          <div className="rounded-2xl bg-white shadow-lg border border-emerald-200 px-6 py-5 flex items-center justify-between">
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-xl bg-emerald-700 flex items-center justify-center shadow-md">
                <span className="text-white font-extrabold text-lg">E</span>
              </div>
              <div>
                <div className="text-xs text-gray-500 font-semibold uppercase tracking-wide">
                  Engineer Dashboard
                </div>
                <h1 className="text-xl md:text-2xl font-extrabold text-emerald-800">
                  {name}
                </h1>
                <div className="mt-1 text-[11px] text-slate-500">
                  Login: {user.email}
                </div>
              </div>
            </div>
            <div className="flex flex-col items-end gap-2">
              <button
                onClick={doLogout}
                className="text-xs px-3 py-2 rounded-lg bg-gray-900 text-white shadow hover:bg-black"
              >
                Logout
              </button>
              <Link to="/" className="text-[11px] text-emerald-700 underline">
                ← Back to Main Site
              </Link>
            </div>
          </div>

          {/* STATS */}
          <div className="grid md:grid-cols-4 gap-3">
            <StatCard label="Total Projects" value={totals.total} note="All awarded projects." />
            <StatCard label="Not Started" value={totals.notStarted} />
            <StatCard label="In Progress" value={totals.inProgress} />
            <StatCard label="Completed (≈100%)" value={totals.completed} />
          </div>

          {/* PROJECT LIST */}
          <div className="bg-white rounded-2xl shadow border border-emerald-100 p-4">
            <h2 className="text-sm font-bold text-emerald-800 mb-3">
              Awarded Godown Projects
            </h2>

            {loadingProjects ? (
              <div className="text-[12px] text-slate-500">Loading projects…</div>
            ) : projects.length === 0 ? (
              <div className="text-[12px] text-slate-500">
                No projects available.
              </div>
            ) : (
              <div className="space-y-3">
                {projects.map((pr) => {
                  const prog = Number(pr.progress || 0);
                  const stageLabel = prog >= 95 ? "Completed" : prog > 0 ? "In Progress" : "Not Started";
                  const badgeColor = prog >= 95 ? "green" : prog > 0 ? "blue" : "gray";

                  return (
                    <div key={pr.id} className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="text-xs text-slate-500 font-semibold uppercase">
                            {pr.pacsName || "PACS / Society"}
                          </div>
                          <div className="text-sm font-bold text-slate-900">
                            {pr.projectTitle || "Godown Project"}
                          </div>
                          <div className="mt-1 text-[11px] text-slate-600">
                            {pr.district || "—"}, {pr.state || "—"}
                          </div>
                        </div>

                        <div className="text-right text-[11px] text-slate-600 space-y-1">
                          {pr.capacityMt != null && (
                            <div><b>Capacity:</b> {pr.capacityMt} MT</div>
                          )}
                          <div><b>Engineer RA Bills:</b> {pr.raCount}</div>
                          {pr.raTotal > 0 && (
                            <div><b>Total:</b> {formatINR(pr.raTotal)}</div>
                          )}
                        </div>
                      </div>

                      <div className="mt-2 flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 flex-1">
                          <ProgressBar value={prog} />
                          <div className="w-10 text-right text-[11px] font-semibold text-slate-700">
                            {prog}%
                          </div>
                        </div>
                        <Badge color={badgeColor}>{stageLabel}</Badge>
                      </div>

                      <div className="mt-3 flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => navigate(`/tender/engineer-measurement/${pr.id}`)}
                          className="px-3 py-1.5 rounded-full bg-emerald-700 text-white text-[11px] font-semibold shadow-sm hover:bg-emerald-800"
                        >
                          Engineer Measurement
                        </button>

                        <button
                          type="button"
                          onClick={() => navigate(`/tender/engineer-ra`)}
                          className="px-3 py-1.5 rounded-full border border-emerald-500 bg-white text-[11px] font-semibold text-emerald-700 hover:bg-emerald-50"
                        >
                          Engineer RA Bills
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
